import { useEffect, useState, useRef } from 'react';
import { ArrowDown, ArrowUp } from '@coolblue-development/icons';
import { useAppContext } from '../../../AppContext';
import { useTelephonyContext } from '../../../contexts/TelephonyContext';
import { Button, Menu, MenuItem } from '@coolblue-development/freddy';

const StatusMenu: React.FC = () => {
  const { agentState } = useAppContext();
  const { setTelephonyStatusBarOpen } = useTelephonyContext();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [agentStates, setAgentStates] = useState<connect.AgentStateDefinition[]>([]);
  const [stateNames, setStateNames] = useState<string[]>([]);
  const [currentState, setCurrentState] = useState<string>('');
  const nextCurrentStateRef = useRef<string | undefined>(undefined);

  const handleClick = (): void => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  useEffect(() => {
    connect.agent(function (agent) {
      const agentStates = agent.getAgentStates();
      const stateNames = agentStates.map(state => state.name);
      setAgentStates(agentStates);
      setStateNames(stateNames);
      setCurrentState(agent.getAvailabilityState().state);

      agent.onEnqueuedNextState(() => {
        const nextState = agent.getNextState();
        nextCurrentStateRef.current = nextState?.name;
      });

      connect.contact(contact => {
        contact.onDestroy(() => {
          const activeContacts = agent.getContacts();
          if (activeContacts.length === 0 && nextCurrentStateRef.current) {
            setCurrentState(nextCurrentStateRef.current);
            nextCurrentStateRef.current = undefined;
          }
        });
      });
    });
  }, []);

  const handleMenuItemClick = (stateName: string): void => {
    const selectedState = agentStates.find(state => state.name === stateName);
    if (!selectedState) {
      return;
    }

    connect.agent(agent => {
      const activeContacts = agent.getContacts();

      if (activeContacts.length === 0 || activeContacts[0]?.getStatus().type === connect.ContactStateType.ENDED) {
        agent.setState(selectedState);
        nextCurrentStateRef.current = undefined;
        setCurrentState(stateName);
      } else {
        const agentSetStateOptions: connect.AgentSetStateOptions = {
          enqueueNextState: true
        };

        agent.setState(
          selectedState,
          {
            success: () => {
              nextCurrentStateRef.current = selectedState.name;
              setCurrentState('Next status: ' + selectedState.name);
            },
            failure: () => {
              setCurrentState(
                nextCurrentStateRef.current
                  ? 'Next status: ' + nextCurrentStateRef.current
                  : agent.getAvailabilityState().state
              );
            }
          },
          agentSetStateOptions
        );
      }
    });

    handleClose();
  };

  useEffect(() => {
    if (agentState?.type === connect.AgentStateType.NOT_ROUTABLE) {
      setTelephonyStatusBarOpen(true);
    } else if (
      agentState?.type === connect.AgentStateType.ROUTABLE ||
      agentState?.type === connect.AgentStateType.OFFLINE
    ) {
      setTelephonyStatusBarOpen(false);
    }
  }, [agentState, setTelephonyStatusBarOpen]);

  return (
    <>
      <Button
        ref={buttonRef}
        endIcon={open ? <ArrowUp /> : <ArrowDown />}
        sx={{
          marginRight: 3,
          background: 'transparent',
          color: 'white',
          boxShadow: 'none',
          textAlign: 'left',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)', boxShadow: 'none' }
        }}
        onClick={handleClick}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        {currentState}
      </Button>
      <Menu
        referenceElement={buttonRef.current}
        open={open}
        onClose={handleClose}
        placement="bottom-end"
        style={{ zIndex: 10000 }}
      >
        {stateNames.map((state, index) => (
          <MenuItem key={index} onClick={() => handleMenuItemClick(state)}>
            {state}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default StatusMenu;
